import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserBehaviorTable = ({ users }) => {
  const [sortField, setSortField] = useState('lastActive');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedUsers = [...users]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;

    if (typeof aValue === 'string') {
      return aValue?.localeCompare(bValue) * modifier;
    }
    return (aValue - bValue) * modifier;
  });

  const totalPages = Math.ceil(sortedUsers?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = sortedUsers?.slice(startIndex, startIndex + itemsPerPage);

  const getTierBadge = (tier) => {
    return tier === 'Pro' ?'bg-primary/10 text-primary' :'bg-muted text-muted-foreground';
  };

  const getEngagementColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 50) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h3 className="text-lg font-semibold text-foreground">User Behavior Details</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
            Filter
          </Button>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  User
                  <Icon
                    name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('tier')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  Tier
                  <Icon
                    name={sortField === 'tier' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('engagement')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  Engagement
                  <Icon
                    name={sortField === 'engagement' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('transactions')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  Transactions
                  <Icon
                    name={sortField === 'transactions' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('totalSpend')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  Total Spend
                  <Icon
                    name={sortField === 'totalSpend' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-left py-3 px-4">
                <button
                  onClick={() => handleSort('lastActive')}
                  className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-smooth caption"
                >
                  Last Active
                  <Icon
                    name={sortField === 'lastActive' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
                    size={14}
                  />
                </button>
              </th>
              <th className="text-right py-3 px-4">
                <span className="text-sm font-semibold text-muted-foreground caption">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers?.map((user) => (
              <tr
                key={user?.id}
                className="border-b border-border hover:bg-muted/20 transition-smooth"
              >
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {user?.name}
                      </div>
                      <div className="text-xs text-muted-foreground caption truncate">
                        {user?.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getTierBadge(user?.tier)}`}>
                    {user?.tier}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold font-data ${getEngagementColor(user?.engagement)}`}>
                      {user?.engagement}%
                    </span>
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${user?.engagement >= 80 ? 'bg-success' : user?.engagement >= 50 ? 'bg-warning' : 'bg-error'}`}
                        style={{ width: `${user?.engagement}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm font-data text-foreground">
                    {user?.transactions?.toLocaleString()}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm font-data font-semibold text-foreground">
                    ${user?.totalSpend?.toLocaleString()}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm text-muted-foreground caption">
                    {user?.lastActive}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="p-2 rounded-lg hover:bg-muted/50 transition-smooth"
                      aria-label="View user details"
                    >
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      className="p-2 rounded-lg hover:bg-muted/50 transition-smooth"
                      aria-label="More options"
                    >
                      <Icon name="MoreVertical" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground caption">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedUsers?.length)} of {sortedUsers?.length} users
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-smooth ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted/50 text-muted-foreground'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserBehaviorTable;